import reactStringReplace from 'react-string-replace';
import { startCase } from 'es-toolkit';

import { Anchor, Button, Text, TextButton } from '@zillow/constellation-10';

import { preWrapTextStyles } from '../styles/text';

import type { ComponentProps } from 'react';
import type {
  GetUserPropertyDetailsLocaleSpecificNoticeWebDto,
  GetUserPropertyDetailsTotalMonthlyPriceWithFeeNoticeWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

// NOTE: fontTypes and button types are returned by RPro backend in v8 format, but we map them to v10
const BUTTON_TYPES = {
  primary: 'primary',
  secondary: 'secondary',
  tertiary: 'tertiary',
  caution: 'caution',
} as const;

const FONT_TYPES = {
  h0: 'h0',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  bodyHeading: 'bodyHeading',
  body: 'body',
  bodySmallHeading: 'bodySmallHeading',
  bodySmall: 'bodySmall',
  finePrintHeading: 'finePrintHeading',
  finePrint: 'finePrint',
  legal: 'legal',
} as const;

export type BuildTextArgs = {
  text: string;
  stylings: Array<string>;
  key: string;
  defaultFontType: string;
  url?: string | null;
  onClick?: (() => void) | null;
  testId?: string | null;
  fsHide?: boolean;
};

const stringHelper = {
  isValidUrl(str: string) {
    let url;
    try {
      url = new URL(str);
    } catch (_) {
      return false;
    }
    return url.protocol === 'http:' || url.protocol === 'https:';
  },
  capitalize(str?: string | null) {
    if (str === undefined || str === null) return '';

    return str.charAt(0).toUpperCase() + str.slice(1);
  },
  sentenceCase(str?: string | null) {
    if (str === undefined || str === null) return '';

    const trimmed = str.trim();
    const shouldNormalizeCase = trimmed === trimmed.toUpperCase() || trimmed === trimmed.toLowerCase();
    const normalized = shouldNormalizeCase ? trimmed.toLowerCase() : trimmed;
    const sentenceCased = normalized.charAt(0).toUpperCase() + normalized.slice(1);

    return sentenceCased.replace(/\bZillow\b/gi, 'Zillow').replace(/\bid\b/gi, 'ID');
  },
  pluralize(num: number | string, singular: string, plural: string) {
    const parsedNum = typeof num === 'string' ? parseInt(num, 10) : num;
    return `${num} ${parsedNum === 1 ? singular : plural}`;
  },
  pluralizeWord(num: number, singular: string, plural: string) {
    return `${num === 1 ? singular : plural}`;
  },
  wrapText(text = '', maxCharacters: number) {
    return `${text.slice(0, maxCharacters)}${text.length >= maxCharacters ? '…' : ''}`;
  },
  sanitizeReactInput(string: string) {
    let parsed = '';

    if (string) {
      parsed = string.replace(/\{|\}/g, '');
    }

    return parsed;
  },
  startCaseRemoveSpace(string: string) {
    return typeof string === 'string' ? startCase(string).replace(/\s/g, '') : '';
  },
  isEmailString(url: string) {
    return url.includes('@') && url.includes('.com') && !url.includes('/');
  },
  updateFontHeading(fontType: string) {
    switch (fontType) {
      case FONT_TYPES.body:
        return FONT_TYPES.bodyHeading;
      case FONT_TYPES.bodySmall:
        return FONT_TYPES.bodySmallHeading;
      case FONT_TYPES.finePrint:
        return FONT_TYPES.finePrintHeading;
      default:
        return fontType;
    }
  },
  getFontType(defaultFontType: string, stylings: Array<string>) {
    let fontType = stylings.filter((style: string) => Object.prototype.hasOwnProperty.call(FONT_TYPES, style))[0];
    if (!fontType) {
      fontType = defaultFontType;
    }
    if (stylings.length > 0 && stylings.includes('emphasize')) {
      fontType = stringHelper.updateFontHeading(fontType);
    }
    return fontType;
  },
  mapFontTypeToTextStyle(fontType: string): ComponentProps<typeof Text>['textStyle'] {
    const mapping: Record<string, ComponentProps<typeof Text>['textStyle']> = {
      [FONT_TYPES.h1]: 'heading-xl',
      [FONT_TYPES.h2]: 'heading-lg',
      [FONT_TYPES.h3]: 'heading-md',
      [FONT_TYPES.h4]: 'heading-sm',
      [FONT_TYPES.h5]: 'heading-xs',
      [FONT_TYPES.h6]: 'body-lg',
      [FONT_TYPES.body]: 'body',
      [FONT_TYPES.bodyHeading]: 'body-bold',
      [FONT_TYPES.bodySmall]: 'body-sm',
      [FONT_TYPES.bodySmallHeading]: 'body-sm-bold',
      [FONT_TYPES.finePrint]: 'body-xs',
      [FONT_TYPES.finePrintHeading]: 'body-xs-bold',
    };
    return mapping[fontType] || 'body';
  },
  buildText({
    text,
    stylings,
    key,
    defaultFontType,
    url = null,
    onClick = null,
    testId = null,
    fsHide = false,
  }: BuildTextArgs) {
    const fontType = stringHelper.getFontType(defaultFontType, stylings);
    const buttonType: Array<keyof typeof BUTTON_TYPES | string> = stylings.filter((style: string) =>
      Object.prototype.hasOwnProperty.call(BUTTON_TYPES, style),
    );
    const propsForButton = {
      key,
      onClick: onClick || undefined,
      'data-test': testId,
      'data-testid': testId,
      disabled: stylings.length > 0 ? stylings.includes('disabled') : false,
      fontType,
      ...(buttonType.length > 0 && {
        tone: (buttonType[0] === 'primary' ? 'brand' : 'brand') as 'brand' | 'neutral' | 'critical',
        emphasis: (buttonType[0] === 'primary' ? 'filled' : 'outlined') as 'filled' | 'outlined',
      }),
    };

    if ((stylings.includes('primary') || stylings.includes('secondary')) && !stylings.includes('textLink')) {
      return <Button {...propsForButton}>{text}</Button>;
    }

    return (
      <Text
        textStyle={stringHelper.mapFontTypeToTextStyle(fontType)}
        onClick={onClick || undefined}
        className={fsHide ? 'fs-mask' : undefined}
        key={key}
        css={preWrapTextStyles}
      >
        {url ? (
          <Anchor
            href={stringHelper.isEmailString(url) ? `mailto:${url}` : url}
            target="_blank"
            onClick={propsForButton.onClick}
            data-test={propsForButton['data-test']}
            data-testid={propsForButton['data-testid']}
            disabled={propsForButton.disabled}
            rel="noopener noreferrer"
          >
            {text}
          </Anchor>
        ) : stylings.includes('textLink') ? (
          <TextButton
            onClick={propsForButton.onClick}
            data-test={propsForButton['data-test']}
            data-testid={propsForButton['data-testid']}
            disabled={propsForButton.disabled}
          >
            {text}
          </TextButton>
        ) : (
          text
        )}
      </Text>
    );
  },
  kebabize(string: string) {
    return string
      .split('')
      .map((letter: string, idx: number) => {
        return letter.toUpperCase() === letter ? `${idx !== 0 ? '-' : ''}${letter.toLowerCase()}` : letter;
      })
      .join('');
  },
  renderWithReplacedLinks(
    {
      text,
      mappings,
    }: GetUserPropertyDetailsLocaleSpecificNoticeWebDto | GetUserPropertyDetailsTotalMonthlyPriceWithFeeNoticeWebDto,
    {
      callbacks = {},
      dataEventIds = {},
    }: { callbacks?: { [x: string]: () => void }; dataEventIds?: Record<string, string> } = {},
    generic?: string,
  ) {
    let formattedText: React.ReactNodeArray | string = text;

    mappings.forEach((item) => {
      const { replaceUrl, replaceText } = item;
      const keyForMaps = replaceText === generic ? replaceText : replaceText.split(' ').slice(1).join(' ');

      formattedText = reactStringReplace(formattedText, replaceText, () => (
        <Anchor
          href={replaceUrl}
          target="_blank"
          onClick={callbacks?.[keyForMaps]}
          data-eventid={dataEventIds?.[keyForMaps]}
          rel="noopener noreferrer"
        >
          {replaceText}
        </Anchor>
      ));
    });

    return formattedText;
  },
};

export default stringHelper;
